/**
 * Subscription Service
 *
 * Handles subscription data retrieval from backend
 * Used for polling subscription status after Stripe Checkout completion
 *
 * Issue #327 Phase 3: Stripe Webhook & Subscription Management
 *
 * @created 2025-11-04
 */

import * as Sentry from '@sentry/react';
import env from '../config/env';
import logger from '../utils/logger';
import { safeJsonParse } from '../utils/jsonParseUtils';
import { AuthApiClient } from './auth/shared/apiClient';
import {
  SubscriptionStatusEnum,
  type SubscriptionStatus,
} from '../types/enums';
import type { PlanType } from '../store/useAuthStore';

/**
 * Plan + customer/staff overage status
 * Issue #3308: Free 降格 read-only banner / export prompt
 */
export interface SubscriptionStatusResponse {
  plan: PlanType;
  customerCount: number;
  customerLimit: number | null;
  customerOverage: number;
  staffCount: number;
  staffLimit: number | null;
  staffOverage: number;
}

/**
 * Subscription interface matching backend Subscription entity
 * Issue #1780: Use SubscriptionStatus type from enums
 */
export interface Subscription {
  id: string;
  userId: string;
  stripeCustomerId: string;
  stripeSubscriptionId: string;
  status: SubscriptionStatus;
  planId: 'light' | 'standard' | 'premium';
  currentPeriodStart: string;
  currentPeriodEnd: string;
  cancelAtPeriodEnd: boolean;
  tenantId: string;
  metadata: Record<string, unknown>;
  createdAt: string;
  updatedAt: string;
}

/**
 * API response from GET /api/payments/subscription/:userId
 */
export interface GetSubscriptionResponse {
  subscription: Subscription | null;
}

/**
 * Subscription API error types
 * テスト用にHTTPステータスコードと対応するエラータイプを定義
 * Issue #1788: user_not_found を追加（新規ユーザーセットアップ中の識別用）
 */
export type SubscriptionErrorType =
  | 'auth_expired' // 401 - 認証期限切れ
  | 'user_not_found' // 401 - ユーザー未作成（新規、webhook処理中）
  | 'forbidden' // 403 - アクセス禁止
  | 'not_found' // 404 - 見つからない
  | 'server_error' // 500 - サーバーエラー
  | 'network_error'; // ネットワークエラー

/**
 * Custom error class for subscription API errors
 */
export class SubscriptionError extends Error {
  public readonly type: SubscriptionErrorType;
  public readonly statusCode?: number;

  constructor(
    type: SubscriptionErrorType,
    message: string,
    statusCode?: number
  ) {
    super(message);
    this.name = 'SubscriptionError';
    this.type = type;
    this.statusCode = statusCode;
  }

  /**
   * HTTPステータスコードからエラータイプを判定
   * Issue #1788: 401 + "User not found" は user_not_found として識別（リトライ対象）
   */
  static fromStatusCode(
    statusCode: number,
    message?: string
  ): SubscriptionError {
    const lowerMessage = message?.toLowerCase() || '';

    switch (statusCode) {
      case 401:
        // Issue #1788: "User not found" は新規ユーザーセットアップ中の可能性
        // ログアウトではなくリトライさせる
        if (
          lowerMessage.includes('user not found') ||
          lowerMessage.includes('setup_pending') ||
          lowerMessage.includes('tenant assignment')
        ) {
          return new SubscriptionError(
            'user_not_found',
            'ユーザー設定中です。しばらくお待ちください。',
            401
          );
        }
        return new SubscriptionError(
          'auth_expired',
          message || '認証が期限切れです',
          401
        );
      case 403:
        return new SubscriptionError(
          'forbidden',
          message || '権限がありません',
          403
        );
      case 404:
        return new SubscriptionError(
          'not_found',
          message || '見つかりません',
          404
        );
      case 500:
      default:
        return new SubscriptionError(
          'server_error',
          message || 'サーバーエラーが発生しました',
          statusCode
        );
    }
  }

  /**
   * エラーがリトライ可能かどうかを判定
   * Issue #1788: user_not_found はリトライ可能
   */
  isRetryable(): boolean {
    return this.type === 'user_not_found' || this.type === 'network_error';
  }
}

class SubscriptionService {
  private apiBaseUrl: string;

  constructor() {
    this.apiBaseUrl = env.get('API_BASE_URL') || '';
  }

  /**
   * Get user's active subscription
   *
   * @param userId - User ID (UUID)
   * @returns Active subscription or null
   * @throws Error if API call fails
   */
  async getActiveSubscription(userId: string): Promise<Subscription | null> {
    try {
      // Issue #1425: Use AuthApiClient.getAuthToken() instead of localStorage.getItem('token')
      const token = await AuthApiClient.getAuthToken();
      const response = await fetch(
        `${this.apiBaseUrl}/api/v1/payments/subscription/${userId}`,
        {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
            // Issue #1370: JwtAuthGuard added to backend endpoint
            Authorization: `Bearer ${token}`,
          },
        }
      );

      if (!response.ok) {
        // Issue #1788: エラーボディからメッセージを取得（User not found検出用）
        let errorMessage: string | undefined;
        try {
          const errorData = await response.json();
          errorMessage = errorData.message;
        } catch {
          // JSONパースに失敗した場合はundefined（statusTextは使わない）
        }

        // Issue #1657: Return typed errors for different status codes
        if (response.status === 404) {
          // Issue #1657 C-4: Check if this is "user not found" vs "subscription not found"
          // Note: Only check JSON body message, not statusText
          if (errorMessage?.toLowerCase().includes('user')) {
            throw new SubscriptionError(
              'not_found',
              'ユーザーが見つかりません'
            );
          }
          // No subscription found yet (expected case during polling)
          return null;
        }

        // Issue #1788: 401の場合もエラーボディを解析してuser_not_foundを識別
        // statusTextをフォールバックとして使用
        const error = SubscriptionError.fromStatusCode(
          response.status,
          errorMessage || response.statusText
        );

        // Issue #1788: user_not_foundエラーはSentryに送らない（リトライで解決する可能性）
        if (error.type !== 'user_not_found') {
          Sentry.captureException(error, {
            extra: {
              userId,
              status: response.status,
              statusText: response.statusText,
            },
          });
        }
        throw error;
      }

      const responseData = await response.json();
      // Issue #775: TransformInterceptorがレスポンスを { statusCode, message, data } でラップしている
      // 実際のデータは responseData.data にある
      const data: GetSubscriptionResponse = responseData.data || responseData;
      return data.subscription;
    } catch (error) {
      logger.error('Failed to fetch subscription:', error);

      // Issue #1657: Wrap network errors as SubscriptionError
      if (error instanceof SubscriptionError) {
        throw error;
      }

      // Network error or other fetch failures
      if (error instanceof TypeError && error.message.includes('fetch')) {
        throw new SubscriptionError(
          'network_error',
          'ネットワークエラーが発生しました'
        );
      }

      throw error;
    }
  }

  /**
   * Poll for subscription creation after Checkout completion
   *
   * Polling strategy:
   * - Interval: 5 seconds
   * - Max attempts: 6 (total 30 seconds)
   * - Returns subscription when found, or null after timeout
   *
   * @param userId - User ID (UUID)
   * @param options - Polling options
   * @returns Promise<Subscription | null>
   */
  async pollForSubscription(
    userId: string,
    options: {
      intervalMs?: number;
      maxAttempts?: number;
      totalTimeoutMs?: number;
      onAttempt?: (attempt: number, maxAttempts: number) => void;
    } = {}
  ): Promise<Subscription | null> {
    const intervalMs = options.intervalMs ?? 5000; // Default: 5 seconds
    const maxAttempts = options.maxAttempts ?? 6; // Default: 6 attempts (30 seconds)
    const totalTimeoutMs = options.totalTimeoutMs ?? 30000; // Issue #1657 C-7: Overall timeout

    logger.info(`Starting subscription polling for user ${userId}`, {
      intervalMs,
      maxAttempts,
      totalTimeoutMs,
      totalDuration: `${(intervalMs * maxAttempts) / 1000}s`,
    });

    let lastError: Error | null = null;
    const startTime = Date.now();

    for (let attempt = 1; attempt <= maxAttempts; attempt++) {
      // Issue #1657 C-7: Check overall timeout
      if (Date.now() - startTime > totalTimeoutMs) {
        logger.warn(
          `Subscription polling hit overall timeout after ${totalTimeoutMs}ms`
        );
        break;
      }
      try {
        // Notify caller of current attempt
        options.onAttempt?.(attempt, maxAttempts);

        // Fetch subscription
        const subscription = await this.getActiveSubscription(userId);

        if (subscription) {
          // Issue #1657: unpaid status means Webhook hasn't processed yet, continue polling
          // Issue #1780: Use SubscriptionStatusEnum for type safety
          if (subscription.status === SubscriptionStatusEnum.UNPAID) {
            logger.debug(
              `Subscription found but status is unpaid, continuing polling...`,
              {
                subscriptionId: subscription.id,
                attempt,
                maxAttempts,
              }
            );
            // Continue polling as if no subscription found
          } else {
            logger.info(
              `Subscription found on attempt ${attempt}/${maxAttempts}`,
              {
                subscriptionId: subscription.id,
                status: subscription.status,
                planId: subscription.planId,
              }
            );
            return subscription;
          }
        }

        // No subscription yet, continue polling (unless last attempt)
        if (attempt < maxAttempts) {
          logger.debug(
            `Subscription not found, waiting ${intervalMs}ms before attempt ${attempt + 1}/${maxAttempts}`
          );
          await this.sleep(intervalMs);
        }
      } catch (error) {
        logger.error(
          `Subscription polling failed on attempt ${attempt}/${maxAttempts}:`,
          error
        );

        // Issue #1657: Fatal errors should not continue polling
        if (error instanceof SubscriptionError) {
          // All typed errors are fatal - throw immediately to show user
          // C-4: not_found, C-5: server_error, C-6: network_error
          throw error;
        } else {
          lastError = error instanceof Error ? error : new Error(String(error));
        }

        // Continue polling on recoverable error (unless last attempt)
        if (attempt < maxAttempts) {
          await this.sleep(intervalMs);
        }
      }
    }

    // Timeout: No subscription found after max attempts
    logger.warn(
      `Subscription polling timeout after ${maxAttempts} attempts (${(intervalMs * maxAttempts) / 1000}s)`
    );

    // Issue #1657: If we had errors during polling, throw the last one
    // C-5: server_error, C-6: network_error should be thrown
    if (
      lastError instanceof SubscriptionError &&
      (lastError.type === 'server_error' || lastError.type === 'network_error')
    ) {
      throw lastError;
    }

    return null;
  }

  /**
   * Sleep utility for polling intervals
   * @param ms - Milliseconds to sleep
   */
  private sleep(ms: number): Promise<void> {
    return new Promise((resolve) => setTimeout(resolve, ms));
  }

  /**
   * Check if user has active subscription
   *
   * @param userId - User ID (UUID)
   * @returns true if user has active subscription
   */
  async hasActiveSubscription(userId: string): Promise<boolean> {
    try {
      const subscription = await this.getActiveSubscription(userId);
      // Issue #770: subscription can be undefined (not just null), use truthiness check
      // Issue #1780: Use SubscriptionStatusEnum for type safety
      return (
        !!subscription && subscription.status === SubscriptionStatusEnum.ACTIVE
      );
    } catch {
      return false;
    }
  }

  /**
   * Get subscription plan name in Japanese
   * @param planId - Plan ID (light/standard/premium)
   * @returns Japanese plan name
   */
  getPlanNameJa(planId: 'light' | 'standard' | 'premium'): string {
    const planNames: Record<string, string> = {
      light: 'ライトプラン',
      standard: 'スタンダードプラン',
      premium: 'プレミアムプラン',
    };
    return planNames[planId] || planId;
  }

  /**
   * Get subscription status name in Japanese
   * Issue #1780: Use SubscriptionStatus type for type safety
   * @param status - Subscription status
   * @returns Japanese status name
   */
  getStatusNameJa(status: SubscriptionStatus): string {
    const statusNames: Record<string, string> = {
      [SubscriptionStatusEnum.ACTIVE]: '有効',
      [SubscriptionStatusEnum.CANCELED]: 'キャンセル済み',
      [SubscriptionStatusEnum.PAST_DUE]: '支払い遅延',
      [SubscriptionStatusEnum.UNPAID]: '未払い',
      [SubscriptionStatusEnum.INCOMPLETE]: '支払い未完了',
      [SubscriptionStatusEnum.INCOMPLETE_EXPIRED]: '支払い期限切れ',
      [SubscriptionStatusEnum.PAUSED]: '一時停止中',
    };
    return statusNames[status] || status;
  }

  /**
   * Change subscription plan
   * Issue #327 Phase 4: Settings Subscription管理UI
   *
   * @param userId - User ID (UUID)
   * @param planId - New plan ID (light/standard/premium)
   * @returns Updated subscription
   * @throws Error if API call fails
   */
  async changeSubscriptionPlan(
    userId: string,
    planId: 'light' | 'standard' | 'premium'
  ): Promise<Subscription> {
    try {
      logger.info(`Changing subscription plan for user ${userId} to ${planId}`);

      // Issue #1425: Use AuthApiClient.getAuthToken() instead of localStorage.getItem('token')
      const token = await AuthApiClient.getAuthToken();
      const response = await fetch(
        `${this.apiBaseUrl}/api/v1/payments/subscription/${userId}/plan`,
        {
          method: 'PATCH',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({ planId }),
        }
      );

      if (!response.ok) {
        const errorData = await safeJsonParse(
          response,
          'SubscriptionService.changePlan'
        );
        // Issue #1425: Add Sentry error tracking
        const error = new Error(
          `Failed to change subscription plan: ${response.status} ${response.statusText} - ${errorData.message || ''}`
        );
        Sentry.captureException(error, {
          extra: { userId, planId, status: response.status },
        });
        throw error;
      }

      const subscription: Subscription = await response.json();
      logger.info(`Subscription plan changed successfully:`, {
        subscriptionId: subscription.id,
        newPlan: planId,
      });
      return subscription;
    } catch (error) {
      logger.error('Failed to change subscription plan:', error);
      throw error;
    }
  }

  /**
   * Cancel subscription
   * Issue #327 Phase 4: Settings Subscription管理UI
   *
   * @param userId - User ID (UUID)
   * @param cancelAtPeriodEnd - Cancel at period end (true: end of period, false: immediate)
   * @returns Updated subscription
   * @throws Error if API call fails
   */
  async cancelSubscription(
    userId: string,
    cancelAtPeriodEnd: boolean = true
  ): Promise<Subscription> {
    try {
      logger.info(`Canceling subscription for user ${userId}`, {
        cancelAtPeriodEnd,
      });

      // Issue #1425: Use AuthApiClient.getAuthToken() instead of localStorage.getItem('token')
      const token = await AuthApiClient.getAuthToken();
      const response = await fetch(
        `${this.apiBaseUrl}/api/v1/payments/subscription/${userId}/cancel`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({ cancelAtPeriodEnd }),
        }
      );

      if (!response.ok) {
        const errorData = await safeJsonParse(
          response,
          'SubscriptionService.cancelSubscription'
        );
        // Issue #1425: Add Sentry error tracking
        const error = new Error(
          `Failed to cancel subscription: ${response.status} ${response.statusText} - ${errorData.message || ''}`
        );
        Sentry.captureException(error, {
          extra: { userId, cancelAtPeriodEnd, status: response.status },
        });
        throw error;
      }

      const subscription: Subscription = await response.json();
      logger.info(`Subscription canceled successfully:`, {
        subscriptionId: subscription.id,
        cancelAtPeriodEnd,
      });
      return subscription;
    } catch (error) {
      logger.error('Failed to cancel subscription:', error);
      throw error;
    }
  }

  /**
   * Reactivate subscription
   * Issue #327 Phase 4: Settings Subscription管理UI
   *
   * @param userId - User ID (UUID)
   * @returns Updated subscription
   * @throws Error if API call fails
   */
  async reactivateSubscription(userId: string): Promise<Subscription> {
    try {
      logger.info(`Reactivating subscription for user ${userId}`);

      // Issue #1425: Use AuthApiClient.getAuthToken() instead of localStorage.getItem('token')
      const token = await AuthApiClient.getAuthToken();
      const response = await fetch(
        `${this.apiBaseUrl}/api/v1/payments/subscription/${userId}/reactivate`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
          },
        }
      );

      if (!response.ok) {
        const errorData = await safeJsonParse(
          response,
          'SubscriptionService.reactivateSubscription'
        );
        // Issue #1425: Add Sentry error tracking
        const error = new Error(
          `Failed to reactivate subscription: ${response.status} ${response.statusText} - ${errorData.message || ''}`
        );
        Sentry.captureException(error, {
          extra: { userId, status: response.status },
        });
        throw error;
      }

      const subscription: Subscription = await response.json();
      logger.info(`Subscription reactivated successfully:`, {
        subscriptionId: subscription.id,
      });
      return subscription;
    } catch (error) {
      logger.error('Failed to reactivate subscription:', error);
      throw error;
    }
  }

  /**
   * Create Stripe Customer Portal session
   * Issue #475 Phase 5: Stripe Customer Portal Integration
   *
   * Redirects user to Stripe Customer Portal where they can:
   * - View subscription details
   * - Change payment method
   * - Update billing information
   * - View invoice history
   * - Cancel subscription
   *
   * @returns Portal session URL
   * @throws Error if API call fails (404: Customer not found, 429: Rate limit, 500: Server error)
   */
  async createPortalSession(): Promise<string> {
    try {
      logger.info('Creating Stripe Customer Portal session');

      // Issue #1425: Use AuthApiClient.getAuthToken() instead of localStorage.getItem('token')
      const token = await AuthApiClient.getAuthToken();
      const response = await fetch(
        `${this.apiBaseUrl}/api/v1/payments/subscription/portal-session`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
          },
        }
      );

      if (!response.ok) {
        if (response.status === 404) {
          logger.error('Stripe Customer not found');
          const error = new Error(
            'Stripe Customer not found. Please contact support.'
          );
          Sentry.captureException(error, { extra: { status: 404 } });
          throw error;
        }
        if (response.status === 429) {
          logger.error('Too many requests to Customer Portal');
          const error = new Error('Too many requests. Please try again later.');
          Sentry.captureException(error, { extra: { status: 429 } });
          throw error;
        }
        // Issue #1425: Add Sentry error tracking
        const error = new Error(
          `Failed to create Customer Portal session: ${response.status} ${response.statusText}`
        );
        Sentry.captureException(error, { extra: { status: response.status } });
        throw error;
      }

      const { url } = await response.json();
      logger.info('Customer Portal session created successfully', { url });
      return url;
    } catch (error) {
      logger.error('Failed to create Customer Portal session:', error);
      throw error;
    }
  }

  /**
   * Get tenant plan + customer/staff overage counts
   *
   * Issue #3308: Free 降格 read-only banner / export prompt
   *
   * Backend endpoint: GET /api/v1/subscriptions/status
   * RequestWithTenant 経由で tenantId を controller が抽出するため、
   * 引数は不要 (Authorization header の Bearer token のみ)。
   *
   * @returns Plan + customer/staff の現件数・上限・超過件数
   * @throws Error if API call fails
   */
  async getSubscriptionStatus(): Promise<SubscriptionStatusResponse> {
    try {
      const token = await AuthApiClient.getAuthToken();
      const response = await fetch(
        `${this.apiBaseUrl}/api/v1/subscriptions/status`,
        {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
          },
        }
      );

      if (!response.ok) {
        // Issue #1788: エラーボディから message を読んで user_not_found 識別
        let errorMessage: string | undefined;
        try {
          errorMessage = (await response.json()).message;
        } catch {
          // JSON パース失敗時は status text fallback
        }

        const error = SubscriptionError.fromStatusCode(
          response.status,
          errorMessage || response.statusText
        );

        // user_not_found は polling 中の expected → Sentry 送らない
        if (error.type !== 'user_not_found') {
          Sentry.captureException(error, {
            extra: {
              status: response.status,
              endpoint: 'subscriptions/status',
            },
          });
        }
        throw error;
      }

      const responseData = await response.json();
      // TransformInterceptor が { statusCode, message, data } でラップする (Issue #775)
      const data: SubscriptionStatusResponse =
        responseData.data || responseData;
      return data;
    } catch (error) {
      logger.error('[SubscriptionService] Failed to fetch status:', error);
      if (error instanceof SubscriptionError) throw error;

      // Network error (TypeError 'Failed to fetch') を SubscriptionError に wrap
      if (error instanceof TypeError && error.message.includes('fetch')) {
        const networkError = new SubscriptionError(
          'network_error',
          'ネットワークエラーが発生しました'
        );
        Sentry.captureException(networkError, {
          extra: { endpoint: 'subscriptions/status' },
        });
        throw networkError;
      }

      // 想定外エラーは Sentry に送って再 throw
      Sentry.captureException(error, {
        extra: { endpoint: 'subscriptions/status', unexpected: true },
      });
      throw error;
    }
  }
}

// Export singleton instance
const subscriptionService = new SubscriptionService();
export default subscriptionService;

// Export class for testing
export { SubscriptionService };
